import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import {
  useWorkOrdersQuery,
  type WorkOrderSummary,
} from '@nyos/shared'
import { useYard } from '../../../contexts/YardContext'
import ScreenShell from '../../../components/ScreenShell'

/**
 * Insights index (Plan 26 Phase 1a; D3 — index → drill-in).
 *
 * Lists the facility's work orders; each row drills into the telemetry story
 * route. Rows are work orders, not telemetry rows — a job without a
 * generation still drills in and renders "Telemetry Summary Unavailable".
 */
export default function InsightsIndexPage() {
  const { activeFacilityId } = useYard()
  const workOrders = useWorkOrdersQuery(activeFacilityId)

  const rows = [...(workOrders.data ?? [])].sort((a, b) => {
    const ta = a.acceptedAt ?? a.completedAt ?? ''
    const tb = b.acceptedAt ?? b.completedAt ?? ''
    return tb.localeCompare(ta)
  })

  return (
    <ScreenShell
      title="Insights"
      subtitle="Every engine decision, explained in plain English — per job, per move"
    >
      <div className="h-full overflow-y-auto p-4">
        {workOrders.isPending ? (
          <div className="text-sm text-muted-foreground">Loading jobs…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No work orders on this facility yet.
          </div>
        ) : (
          <div className="grid gap-2 max-w-3xl">
            {rows.map((wo) => (
              <InsightRow key={wo.id} wo={wo} />
            ))}
          </div>
        )}
      </div>
    </ScreenShell>
  )
}

function InsightRow({ wo }: { wo: WorkOrderSummary }) {
  const when = wo.completedAt ?? wo.acceptedAt
  return (
    <Link
      to={`/execution/switch-jobs/${wo.id}/story`}
      className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 hover:bg-hover transition-colors group"
    >
      <div>
        <div className="text-sm font-medium">
          {wo.swlNumber ?? wo.woNumber ?? 'Switch job'}
        </div>
        <div className="text-sm text-muted-foreground">
          {wo.woNumber ?? wo.id}
          {wo.status ? ` · ${wo.status}` : ''}
          {when ? ` · ${new Date(when).toLocaleString()}` : ''}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
    </Link>
  )
}
